import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

import { PORTAL_ACCOUNT_TABLE_SUB_HEADERS } from 'src/app/lib/constants/admin-portal.constants';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { AdminPortalAccountService } from './admin-portal-account.service';
import { AccountDialogData, AdminPortalAccountModalComponent } from './portal-account-modal/portal-account-modal.component';
import { IUserEntity } from 'src/app/core/model/server/vo/IUserEntity.interface';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { RbacService } from 'src/app/services/rbac.service';

@Component({
  selector: 'app-admin-portal-accounts',
  templateUrl: './admin-portal-accounts.component.html',
  styleUrl: './admin-portal-accounts.component.scss',
})
export class AdminPortalAccountsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private search$  = new Subject<string>();

  tableSubHeader = PORTAL_ACCOUNT_TABLE_SUB_HEADERS;

  // ─── State ────────────────────────────────────────────────────────────────
  isLoading  = false;
  userList: IUserEntity[] = [];

  // ─── Pagination ───────────────────────────────────────────────────────────
  currentPage  = 1;
  pageSize     = 10;
  totalItems   = 0;
  totalPages   = 0;

  // ─── Search ───────────────────────────────────────────────────────────────
  searchKeyword = '';

  // ─── Toggle guard ─────────────────────────────────────────────────────────
  togglingUid: number | null = null;

  // ─── Sorting ──────────────────────────────────────────────────────────────
  sort_column = 'dateCreated';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Permissions ──────────────────────────────────────────────────────────
  canViewAdminActions = false;
  canEditAccounts = false;


  constructor(
    private portalAccountService: AdminPortalAccountService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private popUpDialog: PopUpDialogService,
    private dialog: MatDialog,
    private paginationHelper: PaginationHelperService,
    private rbac: RbacService,
  ) {
    this.canViewAdminActions = this.rbac.hasPermission('admin.view');
    this.canEditAccounts     = this.rbac.hasPermission('admin.manage');
  }

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(keyword => {
        this.searchKeyword = keyword;
        this.currentPage   = 1;
        this.loadUsers();
      });

    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch(console.error);
  }

  // ─── Data loading ─────────────────────────────────────────────────────────

  loadUsers(): void {
    this.isLoading = true;
    this.spinner.show().catch(console.error);

    const request = {
      search: this.searchKeyword || undefined,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    this.portalAccountService
      .getUsers(this.currentPage, this.pageSize, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ rows, totalItems, totalPages }) => {
          this.userList = rows.map(user => ({
            ...user,
            isAdminRow: this.isAdminAccount(user),
          }));
          this.totalItems = totalItems;
          this.totalPages = totalPages;
          console.log(this.userList)
        },
        error: err => {
          console.error('Error loading portal users:', err);
          this.toastr.error('Failed to load users. Please try again.');
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Search & Pagination events ───────────────────────────────────────────

  onSearchKeyup(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value;
    this.search$.next(value);
  }

  onLimitChange(event: Event): void {
    this.pageSize    = Number((event.target as HTMLSelectElement).value);
    this.currentPage = 1;
    this.loadUsers();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadUsers();
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(AdminPortalAccountModalComponent, {
      width: '420px',
      disableClose: true,
      data: { mode: 'create' } satisfies AccountDialogData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => { if (saved) { this.currentPage = 1; this.loadUsers(); } });
  }

  openEditDialog(user: IUserEntity): void {
    const dialogRef = this.dialog.open(AdminPortalAccountModalComponent, {
      width: '420px',
      disableClose: true,
      data: { mode: 'edit', user } satisfies AccountDialogData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => {
        if (saved) { this.loadUsers(); }
      });
  }

  // ─── Enable / Disable ─────────────────────────────────────────────────────

  toggleStatusConfirmation(user: IUserEntity): void {
    const enabling = !this.portalAccountService.isEnabled(user.status);
    const action   = enabling ? 'enable' : 'disable';

    const messages = {
      enable:  'Are you sure you want to enable this account?',
      disable: 'Are you sure you want to disable this account?',
    };

    this.popUpDialog.openStatusActionDialog(
      messages[action],
      action.toUpperCase(),
      'CANCEL',
      () => this.executeToggle(user, enabling),
      () => console.log(`${action} cancelled`)
    );
  }

  private executeToggle(user: IUserEntity, enable: boolean): void {
    this.togglingUid = user.uid;
    this.spinner.show().catch(console.error);

    this.portalAccountService
      .toggleUserStatus(user.uid, enable ? 0 : 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.toastr.success(
              'Status for ' + user.username + ' has been successfully updated!'
            );
            this.loadUsers();
          } else {
            this.popUpDialog.openErrorDialog(
              `Failed to ${enable ? 'enable' : 'disable'} account status. Please try again later.`
            );
          }
        },
        error: err => {
          console.error('Toggle error:', err);
          this.popUpDialog.openErrorDialog(
            'Something went wrong. Please try again later.'
          );
        },
        complete: () => {
          this.togglingUid = null;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  /**
   * Returns true if the row's accountType is Admin or Super Admin.
   * Admins cannot edit other admin-level accounts, only their own row and non-admin rows.
   */
  isAdminAccount(user: IUserEntity): boolean {
    if (user.uid === this.currentUserUid) {
      return false;
    }
    return (user.accountType ?? '').toLowerCase().includes('admin');
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  get currentUserUid(): number {
    return this.portalAccountService.currentUserUid;
  }

  isEnabled(user: IUserEntity): boolean {
    return this.portalAccountService.isEnabled(user.status);
  }

  getStatusLabel(user: IUserEntity): string {
    return this.portalAccountService.getStatusLabel(user.status);
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch(console.error);

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type   = type;

    this.loadUsers();
  }

  get showingText(): string {
    if (this.totalItems === 0) {
      return 'No entries found';
    }
    const displaying = Math.min(this.pageSize, this.totalItems - (this.currentPage - 1) * this.pageSize);
    return `Showing ${displaying} of ${this.totalItems} entries`;
  }
}
